import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

const LandingPage: React.FC = () => {
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const howItWorks = [
    {
      step: '1',
      title: 'Create the song',
      description: 'Add the title, writers, roles, and proposed splits.'
    },
    {
      step: '2',
      title: 'Invite collaborators',
      description: 'Send confirmations by email or SMS — mobile-friendly by default.'
    },
    {
      step: '3',
      title: 'Confirm',
      description: 'Each collaborator reviews and confirms their share.'
    },
    {
      step: '4',
      title: 'Everything is locked',
      description: 'One agreed version of the truth — ready for release.'
    }
  ];

  return (
    <div className="min-h-screen bg-[#18181B]">
      {/* Header */}
      <header className="bg-[#27272A]/30 backdrop-blur-sm border-b border-[#3F3F46] sticky top-0 z-40">
        <div className="container mx-auto px-4 py-4">
          <div className="flex justify-between items-center">
            <button 
              onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
              className="flex items-center"
            >
              <img src="/fulllogo.png" alt="Cotune" className="h-7 sm:h-8 w-auto" />
            </button>
            
            {/* Mobile Menu Button */}
            <button
              onClick={() => setMobileMenuOpen(true)}
              className="md:hidden p-2 text-[#9CA3AF] hover:text-white hover:bg-[#14B8A6]/10 rounded-lg transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center"
              aria-label="Open menu"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center space-x-6">
              <a
                href="#how-it-works"
                className="text-[#9CA3AF] hover:text-white transition-colors"
              >
                How it works
              </a>
              <a
                href="#faq"
                className="text-[#9CA3AF] hover:text-white transition-colors"
              >
                FAQs
              </a>
              <button
                onClick={() => navigate('/login')}
                className="text-[#9CA3AF] hover:text-white transition-colors"
              >
                Log In
              </button>
              <button
                onClick={() => { navigate('/register'); window.scrollTo({ top: 0, behavior: 'auto' }); }}
                className="btn-primary"
              >
                Start free
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <div className="fixed inset-0 z-50 md:hidden">
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-black/60 backdrop-blur-sm"
            onClick={() => setMobileMenuOpen(false)}
          />
          
          {/* Full-screen Menu */}
          <div className="fixed inset-0 bg-[#18181B] flex flex-col">
            {/* Menu Header */}
            <div className="flex items-center justify-between p-4 border-b border-[#3F3F46]">
              <img src="/fulllogo.png" alt="Cotune" className="h-7 w-auto" />
              <button
                onClick={() => setMobileMenuOpen(false)}
                className="p-2 text-[#9CA3AF] hover:text-white hover:bg-[#14B8A6]/10 rounded-lg transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center"
                aria-label="Close menu"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Menu Links */}
            <nav className="flex-1 p-6 space-y-2">
              <a
                href="#how-it-works"
                onClick={() => setMobileMenuOpen(false)}
                className="block px-4 py-4 text-lg text-[#9CA3AF] hover:text-white hover:bg-[#14B8A6]/10 rounded-lg transition-colors"
              >
                How it works
              </a>
              <a
                href="#faq"
                onClick={() => setMobileMenuOpen(false)}
                className="block px-4 py-4 text-lg text-[#9CA3AF] hover:text-white hover:bg-[#14B8A6]/10 rounded-lg transition-colors"
              >
                FAQs
              </a>
              <button
                onClick={() => { setMobileMenuOpen(false); navigate('/login'); }}
                className="w-full text-left px-4 py-4 text-lg text-[#9CA3AF] hover:text-white hover:bg-[#14B8A6]/10 rounded-lg transition-colors"
              >
                Log In
              </button>
            </nav>

            {/* CTA Button */}
            <div className="p-6 border-t border-[#3F3F46]">
              <button
                onClick={() => { setMobileMenuOpen(false); navigate('/register'); window.scrollTo({ top: 0, behavior: 'auto' }); }}
                className="btn-primary w-full py-4 text-lg"
              >
                Start free
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Hero Section */}
      <section className="py-20 sm:py-32 md:py-40 relative overflow-hidden min-h-screen flex items-center">
        {/* Background image with parallax effect */}
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat bg-scroll md:bg-fixed"
          style={{ backgroundImage: 'url(/portal_image.jpg)' }}
        ></div>
        {/* Dark overlay for text readability */}
        <div className="absolute inset-0 bg-[#18181B]/75"></div>
        <div className="container mx-auto px-4 text-center relative z-10">
          <h1 className="text-4xl sm:text-5xl md:text-7xl font-bold text-white mb-6 sm:mb-8">
            Confirm song splits before release.
          </h1>
          <p className="text-xl sm:text-2xl md:text-3xl text-[#E5E7EB] mb-6 sm:mb-8 max-w-4xl mx-auto">
            CoTune helps collaborators agree song splits early — with confirmations and clean data that avoids disputes and missed royalties.
          </p>
          <p className="text-base sm:text-lg md:text-xl text-[#9CA3AF] mb-10 sm:mb-14 max-w-2xl mx-auto px-4">
            No spreadsheets. No chasing. No surprises after release.
          </p>
          <button
            onClick={() => { navigate('/register'); window.scrollTo({ top: 0, behavior: 'auto' }); }}
            className="btn-primary text-base sm:text-lg px-10 sm:px-12 py-4 sm:py-5"
          >
            Start free
          </button>
        </div>
      </section>

      {/* Problem → Solution Section */}
      <section className="py-20 bg-[#27272A]">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="grid md:grid-cols-2 gap-6 md:gap-10">
              {/* The Problem - quieter styling */}
              <div className="bg-[#18181B]/80 border border-[#3F3F46]/60 rounded-2xl p-6 md:p-8 relative overflow-hidden">
                <div className="absolute top-0 left-0 w-1 h-full bg-red-500/40"></div>
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-9 h-9 bg-red-500/10 rounded-lg flex items-center justify-center">
                    <svg className="w-4 h-4 text-red-400/70" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                  </div>
                  <h3 className="text-lg font-semibold text-[#E5E7EB]">The problem</h3>
                </div>
                <p className="text-[#9CA3AF] text-base mb-5">
                  Most split problems don't come from bad intentions — they come from timing.
                </p>
                <ul className="space-y-3 text-[#71717A] text-sm">
                  <li className="flex items-start gap-3">
                    <span className="text-red-400/50 mt-0.5">✕</span>
                    <span>Songs released before splits are agreed</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-red-400/50 mt-0.5">✕</span>
                    <span>Conversations scattered across WhatsApp, email, and notes</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-red-400/50 mt-0.5">✕</span>
                    <span>No single source of truth</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-red-400/50 mt-0.5">✕</span>
                    <span>Fixing it later is slow and awkward</span>
                  </li>
                </ul>
              </div>

              {/* The Solution - stronger visual anchor */}
              <div className="bg-[#18181B] border border-[#14B8A6]/30 rounded-2xl p-6 md:p-8 relative overflow-hidden shadow-lg shadow-[#14B8A6]/5">
                <div className="absolute top-0 left-0 w-1 h-full bg-[#14B8A6]"></div>
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-10 h-10 bg-[#14B8A6]/20 rounded-lg flex items-center justify-center">
                    <svg className="w-5 h-5 text-[#14B8A6]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <h3 className="text-xl font-bold text-white">The CoTune approach</h3>
                </div>
                <p className="text-[#14B8A6] text-lg mb-5">
                  Make split confirmation part of the release process.
                </p>
                <ul className="space-y-3 text-[#9CA3AF]">
                  <li className="flex items-start gap-3">
                    <span className="text-[#10B981] mt-0.5">✓</span>
                    <span>Agree ownership before release</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-[#10B981] mt-0.5">✓</span>
                    <span>Everyone aligned from day one</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-[#10B981] mt-0.5">✓</span>
                    <span>One place to create, invite, confirm, and lock</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-[#10B981] mt-0.5">✓</span>
                    <span>Clean, confirmed data — no disputes later</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works + Benefits - Combined section with background */}
      <section id="how-it-works" className="py-12 relative overflow-hidden">
        {/* Background image */}
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat bg-scroll md:bg-fixed"
          style={{ backgroundImage: 'url(/portal_image1.jpg)' }}
        ></div>
        {/* Dark overlay */}
        <div className="absolute inset-0 bg-[#18181B]/85"></div>
        
        <div className="container mx-auto px-4 relative z-10">
          {/* How It Works */}
          <div className="mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-12 text-center">
              How it works
            </h2>
            {/* Desktop layout */}
            <div className="hidden md:block max-w-5xl mx-auto">
              <div className="grid grid-cols-4 gap-0">
                {howItWorks.map((item, index) => (
                  <div key={index} className="relative flex flex-col items-center text-center px-6">
                    {/* Connecting line - horizontal */}
                    {index < howItWorks.length - 1 && (
                      <div className="absolute top-6 left-1/2 w-full h-[2px] bg-[#3F3F46]" />
                    )}
                    {/* Step number circle */}
                    <div className="relative z-10 w-12 h-12 bg-[#14B8A6] rounded-full flex items-center justify-center text-[#18181B] font-bold text-xl mb-4">
                      {item.step}
                    </div>
                    <h3 className="text-lg font-semibold text-white mb-2">
                      {item.title}
                    </h3>
                    <p className="text-[#E4E4E7] text-sm">{item.description}</p>
                  </div>
                ))}
              </div>
            </div>
            {/* Mobile swipable layout */}
            <div className="md:hidden overflow-x-auto scrollbar-hide -mx-4 px-4">
              <div className="flex gap-4 pb-4" style={{ width: 'max-content' }}>
                {howItWorks.map((item, index) => (
                  <div key={index} className="w-64 flex-shrink-0 bg-[#18181B]/60 backdrop-blur-sm border border-[#3F3F46] rounded-2xl p-6">
                    <div className="w-12 h-12 bg-[#14B8A6] rounded-full flex items-center justify-center text-[#18181B] font-bold text-xl mb-4">
                      {item.step}
                    </div>
                    <h3 className="text-lg font-semibold text-white mb-2">
                      {item.title}
                    </h3>
                    <p className="text-[#E4E4E7] text-sm">{item.description}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* For Artists & Managers */}
          <div className="mt-16">
            <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
              {/* Artists Column */}
              <div className="bg-[#27272A]/80 backdrop-blur-sm border border-[#3F3F46] rounded-2xl p-8">
                <h3 className="text-2xl font-bold text-white mb-6">
                  For artists & writers
                </h3>
                <ul className="space-y-4 text-[#E4E4E7]">
                  <li className="flex items-start">
                    <span className="text-[#10B981] mr-3 mt-1">✓</span>
                    <span>Know your splits are agreed before release</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-[#10B981] mr-3 mt-1">✓</span>
                    <span>Protect your royalties from day one</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-[#10B981] mr-3 mt-1">✓</span>
                    <span>Confirm everything from your phone</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-[#10B981] mr-3 mt-1">✓</span>
                    <span>Free to use</span>
                  </li>
                </ul>
                <p className="text-[#E4E4E7] mt-6 pt-6 border-t border-[#3F3F46]">
                  CoTune keeps things simple — no industry jargon, no admin overhead.
                </p>
              </div>

              {/* Managers Column */}
              <div className="bg-[#27272A]/80 backdrop-blur-sm border border-[#3F3F46] rounded-2xl p-8">
                <h3 className="text-2xl font-bold text-white mb-6">
                  For managers & teams
                </h3>
                <ul className="space-y-4 text-[#E4E4E7]">
                  <li className="flex items-start">
                    <span className="text-[#10B981] mr-3 mt-1">✓</span>
                    <span>Manage splits across multiple artists</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-[#10B981] mr-3 mt-1">✓</span>
                    <span>One consistent process for every release</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-[#10B981] mr-3 mt-1">✓</span>
                    <span>Confirmations with a clear audit trail</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-[#10B981] mr-3 mt-1">✓</span>
                    <span>Less chasing, less risk, less admin</span>
                  </li>
                </ul>
                <p className="text-[#E4E4E7] mt-6 pt-6 border-t border-[#3F3F46]">
                  Built to scale as your roster grows.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Built for Clean Data */}
      <section className="py-20 bg-[#27272A]">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
              Built for clean data
            </h2>
            <p className="text-lg text-[#9CA3AF] mb-6">
              Clean split data makes life easier for everyone involved in a release.
            </p>
            <p className="text-lg text-[#9CA3AF]">
              By confirming ownership early, CoTune helps reduce disputes, delays, and missing registrations — so downstream processes can run smoothly.
            </p>
          </div>
        </div>
      </section>

      {/* Start with Confidence CTA */}
      <section className="py-20 bg-[#18181B]">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
            Start with confidence
          </h2>
          <p className="text-lg text-[#9CA3AF] mb-10 max-w-2xl mx-auto">
            Whether you're releasing your first track or managing multiple artists, CoTune gives you clarity before release — not damage control after.
          </p>
          <button
            onClick={() => { navigate('/register'); window.scrollTo({ top: 0, behavior: 'auto' }); }}
            className="btn-primary text-lg px-10 py-4"
          >
            Create your first song
          </button>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="py-16 relative overflow-hidden min-h-screen">
        {/* Background image */}
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat bg-scroll md:bg-fixed"
          style={{ backgroundImage: 'url(/portal_image0.jpg)' }}
        ></div>
        {/* Dark overlay for text readability */}
        <div className="absolute inset-0 bg-[#27272A]/85"></div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-white mb-4">
              Common Questions
            </h2>
            <p className="text-xl text-[#9CA3AF]">
              Everything you need to know about song splits
            </p>
          </div>
          <div className="max-w-3xl mx-auto space-y-4">
            {/* FAQ Item 1 */}
            <details className="bg-[#18181B] border border-[#3F3F46] rounded-2xl p-6 group cursor-pointer">
              <summary className="flex justify-between items-center text-lg font-semibold text-white list-none">
                <span>What is a song split?</span>
                <span className="text-[#14B8A6] group-open:rotate-45 transition-transform">+</span>
              </summary>
              <p className="mt-4 text-[#9CA3AF]">
                A song split is the percentage of ownership each contributor (songwriter, producer, etc.) holds in a song. 
                It determines how royalties and revenue are distributed when the song generates income from streaming, 
                licensing, sync placements, and more.
              </p>
            </details>

            {/* FAQ Item 2 */}
            <details className="bg-[#18181B] border border-[#3F3F46] rounded-2xl p-6 group cursor-pointer">
              <summary className="flex justify-between items-center text-lg font-semibold text-white list-none">
                <span>Why do I need a split agreement?</span>
                <span className="text-[#14B8A6] group-open:rotate-45 transition-transform">+</span>
              </summary>
              <p className="mt-4 text-[#9CA3AF]">
                A written split agreement protects all collaborators by clearly documenting who owns what percentage of a song. 
                Without one, disputes can arise later when the song starts generating revenue, potentially leading to legal 
                issues and damaged relationships. Getting it in writing early prevents misunderstandings.
              </p>
            </details>

            {/* FAQ Item 3 */}

            {/* FAQ Item 4 */}
            <details className="bg-[#18181B] border border-[#3F3F46] rounded-2xl p-6 group cursor-pointer">
              <summary className="flex justify-between items-center text-lg font-semibold text-white list-none">
                <span>How do confirmations work?</span>
                <span className="text-[#14B8A6] group-open:rotate-45 transition-transform">+</span>
              </summary>
              <p className="mt-4 text-[#9CA3AF]">
                When you create a split, you can send confirmation requests to all collaborators. Each person 
                reviews the split details and confirms their share via email. This creates a clear record that 
                everyone agreed to the terms.
              </p>
            </details>

            {/* FAQ Item 5 */}
            <details className="bg-[#18181B] border border-[#3F3F46] rounded-2xl p-6 group cursor-pointer">
              <summary className="flex justify-between items-center text-lg font-semibold text-white list-none">
                <span>How do I register my splits with a PRO?</span>
                <span className="text-[#14B8A6] group-open:rotate-45 transition-transform">+</span>
              </summary>
              <p className="mt-4 text-[#9CA3AF]">
                After creating your split agreement on Cotune, you can download the PDF and use it as documentation 
                when registering your song with PROs like ASCAP, BMI, SESAC, or PRS. Each collaborator should register 
                their respective share with their own PRO to collect performance royalties.
              </p>
            </details>

            {/* FAQ Item 6 */}
            <details className="bg-[#18181B] border border-[#3F3F46] rounded-2xl p-6 group cursor-pointer">
              <summary className="flex justify-between items-center text-lg font-semibold text-white list-none">
                <span>Can I edit a split after it's created?</span>
                <span className="text-[#14B8A6] group-open:rotate-45 transition-transform">+</span>
              </summary>
              <p className="mt-4 text-[#9CA3AF]">
                Yes, you can edit splits before they are confirmed. Once all collaborators have confirmed the agreement, 
                the split becomes locked to maintain the integrity of the record. If changes are needed after confirmation, 
                you'll need to create a new split agreement with the updated terms and get new confirmations.
              </p>
            </details>

            {/* FAQ Item 7 */}
            <details className="bg-[#18181B] border border-[#3F3F46] rounded-2xl p-6 group cursor-pointer">
              <summary className="flex justify-between items-center text-lg font-semibold text-white list-none">
                <span>What's the difference between master and publishing splits?</span>
                <span className="text-[#14B8A6] group-open:rotate-45 transition-transform">+</span>
              </summary>
              <p className="mt-4 text-[#9CA3AF]">
                Master splits refer to ownership of the actual recording (the master), typically involving artists, 
                producers, and labels. Publishing splits refer to ownership of the underlying composition (lyrics and melody), 
                typically involving songwriters and publishers. Cotune helps you document both types of splits clearly.
              </p>
            </details>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#18181B] text-white py-8 border-t border-[#3F3F46]">
        <div className="container mx-auto px-4">
          <div className="text-center">
            <div className="flex items-center justify-center mb-4">
              <img src="/fulllogo.png" alt="Cotune" className="h-6 w-auto" />
            </div>
            <p className="text-[#9CA3AF] mb-4 text-sm sm:text-base">
              Song splits made simple for independent musicians
            </p>
            <div className="grid grid-cols-2 sm:flex sm:flex-wrap sm:justify-center gap-3 sm:gap-6 text-sm text-[#9CA3AF] mb-4">
              <a href="#faq" className="hover:text-white transition-colors py-2">FAQs</a>
              <button className="hover:text-white transition-colors py-2">Privacy Policy</button>
              <button className="hover:text-white transition-colors py-2">Terms of Service</button>
            </div>
            <div className="mt-4 pt-4 border-t border-[#3F3F46] text-sm text-[#9CA3AF]">
              © 2025 CoTune. All rights reserved.
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
